import React, { useEffect } from 'react';
import { ApplicationType, Fields } from 'src/components/CustomisationPage/feature/customisation.types';
import { useFieldValue } from 'src/core/forms';
import { Field } from 'src/components/Forms/Field';
import { Radio } from 'src/components/Forms/Radio';
import { LogoUploaderFormConnector } from 'src/components/CustomisationPage/LogoUploader';
import { FontPreviewConnected } from 'src/components/CustomisationPage/FontPreview';
import { RadioGroup } from 'src/core/forms';
import { CommonFooter } from 'src/components/CustomisationPage/steps/CommonFooter';
import classnames from 'classnames';
import { BackgroundStyles } from 'src/components/BundleProduct/MultipleStepCustomisation/customisations.types';
import { useDispatch } from 'react-redux';
import { HAVE_MY_LOGO } from 'src/constants/artwork';

import { CustomisationMsg } from '../feature/customisation.actions';

import classes from './ConfigureFields.scss';

type Props = {
    isBundleView?: boolean;
    isActive: boolean;
    backgroundStyles?: BackgroundStyles;
    fieldIndex?: number;
    type: 'contact' | typeof HAVE_MY_LOGO;
    description: string;
};

const DefaultField: React.FC<Props> = ({ isBundleView, isActive, backgroundStyles, fieldIndex, type, description }) => (
    <Field class={classes.noOverflow}>
        <div
            className={classnames({
                [classes.contactWrapper]: !isBundleView,
                [classes.isActive]: isActive,
            })}
        >
            {!isBundleView && type === 'contact' && <p className={classes.alternatively}>alternatively...</p>}
            <label
                className={classes.contactRadio}
                style={backgroundStyles}
                htmlFor={fieldIndex ? `${fieldIndex}${type}` : type}
            >
                {!isBundleView &&
                    (isActive ? (
                        <img
                            className={classes.checkIcon}
                            src={require('../../../svg/check-circle.svg')}
                            alt="check circle"
                        />
                    ) : (
                        <div className={classes.emptyCircle}></div>
                    ))}
                <Radio label={description} value={type} id={fieldIndex ? `${fieldIndex}${type}` : type} />
            </label>
            {isActive && isBundleView && type === 'contact' && (
                <p>The team will email you after purchase to collect your logo and discuss.</p>
            )}
        </div>
    </Field>
);

type ConfigureFieldsProps = {
    applicationType: ApplicationType;
    initialValues: { [index: string]: any };
    fieldPrefix?: string;
    fieldIndex?: number;
    isBundleView?: boolean;
    backgroundStyles?: BackgroundStyles;
    locations?: string[];
};

export const ConfigureFields: React.FC<ConfigureFieldsProps> = ({
    fieldIndex,
    applicationType,
    initialValues,
    fieldPrefix = '',
    isBundleView,
    backgroundStyles,
    locations,
}) => {
    const field = `${fieldPrefix}${Fields.isContactRequested}`;
    const isContactRequested = useFieldValue(field);
    const showUploader = isContactRequested === 'upload';
    const showContactLater = isContactRequested === 'contact';
    const showHaveMyLogo = isContactRequested === HAVE_MY_LOGO;

    const dispatch = useDispatch();

    useEffect(() => {
        if (locations) {
            dispatch(
                CustomisationMsg('Customisation.EstimatePrices', {
                    locations,
                    isLogoMissing: showContactLater || showHaveMyLogo,
                }),
            );
        }
    }, [dispatch, isContactRequested, locations, showContactLater, showHaveMyLogo]);

    if (applicationType === ApplicationType.Logo) {
        return (
            <RadioGroup field={field} initialValue="upload">
                <Field class={classes.noOverflow}>
                    <label
                        className={classnames({
                            [classes.wrapper]: !isBundleView,
                            [classes.isActive]: showUploader,
                            [classes.isActiveWithStyles]: backgroundStyles && showUploader,
                        })}
                        style={backgroundStyles}
                        htmlFor={fieldIndex ? `${fieldIndex}upload` : 'upload'}
                    >
                        <div className={classes.upload}>
                            {!isBundleView && showUploader && (
                                <img
                                    className={classes.checkIcon}
                                    src={require('../../../svg/check-circle.svg')}
                                    alt="check circle"
                                />
                            )}
                            {!isBundleView && !showUploader && <div className={classes.emptyCircle}></div>}
                            <Radio
                                label={isBundleView ? 'Upload a logo file' : 'Upload your own logo'}
                                value="upload"
                                id={fieldIndex ? `${fieldIndex}upload` : 'upload'}
                            />{' '}
                        </div>
                        {showUploader && (
                            <LogoUploaderFormConnector
                                url={initialValues?.image}
                                fileName={undefined}
                                fieldPrefix={fieldPrefix}
                                isBundleView={isBundleView}
                            />
                        )}
                    </label>
                </Field>
                <DefaultField
                    isBundleView={isBundleView}
                    isActive={showContactLater}
                    backgroundStyles={backgroundStyles}
                    fieldIndex={fieldIndex}
                    type="contact"
                    description={
                        isBundleView
                            ? 'Contact me for a logo'
                            : "Don't have your logo to hand? Don't worry, select here and we will contact after you place your order."
                    }
                />
                <DefaultField
                    isBundleView={isBundleView}
                    isActive={showHaveMyLogo}
                    backgroundStyles={backgroundStyles}
                    fieldIndex={fieldIndex}
                    type={HAVE_MY_LOGO}
                    description="You already have my logo, it’s just not in my account (no setup fee will be charged)"
                />
                <CommonFooter
                    fieldPrefix={fieldPrefix}
                    isBundleView={isBundleView}
                    backgroundStyles={backgroundStyles}
                />
            </RadioGroup>
        );
    }

    return (
        <FontPreviewConnected
            initialValues={initialValues}
            fieldPrefix={fieldPrefix}
            isBundleView={isBundleView}
            backgroundStyles={backgroundStyles}
        />
    );
};
